import React, { useEffect, useRef, useState } from 'react';
import Toolbar, { type ToolbarButton, type ToolbarDropdownConfig } from './index';
import IconButton from '../IconButton';
import { MenuTriangle } from '../Icons';

export interface ToolbarDropdownProps {
  /** Controlled mode: read `open` from the prop instead of local state. */
  useOpenProp?: boolean;
  /** The controlled open state (only read when `useOpenProp`). */
  open?: boolean;
  /** Called when the dropdown requests to close (controlled mode). */
  onRequestClose?: () => void;
  /** The buttons to render inside the dropdown. */
  buttons: ToolbarButton[];
  /** Styling variant. */
  variant?: string;
  /** Render an arrow button to toggle the dropdown? */
  arrow?: boolean;
}

/**
 * Inline replacement for the `react-click-outside` (`enhanceWithClickOutside`)
 * HOC that used to wrap this component — it was the sole consumer of that
 * package, which is removed in this batch.
 *
 * Behaviour is matched byte-for-byte to the HOC:
 *   - It listened on `document` for the `'click'` event in the CAPTURE phase
 *     (`addEventListener('click', handler, true)`) — not mousedown/touchstart.
 *   - It fired the callback only when the click target was outside the wrapped
 *     component's root DOM node (`!domNode.contains(e.target)`).
 * The same event name and capture flag are used here so the close-on-outside
 * timing is identical.
 */
function useOnClickOutside(
  ref: React.RefObject<HTMLDivElement | null>,
  handler: (e: MouseEvent) => void
) {
  // Keep the latest handler in a ref so the listener (registered once on mount,
  // like the HOC's componentDidMount) always calls the current closure without
  // re-subscribing.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const listener = (e: MouseEvent) => {
      const domNode = ref.current;
      if (!domNode || !domNode.contains(e.target as Node)) {
        handlerRef.current(e);
      }
    };
    document.addEventListener('click', listener, true);
    return () => {
      document.removeEventListener('click', listener, true);
    };
  }, [ref]);
}

export default function ToolbarDropdown(props: ToolbarDropdownProps) {
  const { useOpenProp, open: openProp, onRequestClose, buttons, variant, arrow } = props;

  const [open, setOpen] = useState(false);

  // The effective open state: controlled (useOpenProp) reads the prop, otherwise
  // the local state. Mirrors the class `get open()`.
  const isOpen = useOpenProp ? openProp : open;

  const rootRef = useRef<HTMLDivElement>(null);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
  };

  const onButtonClick = (button: ToolbarButton) => {
    if (onRequestClose) {
      onRequestClose();
    } else {
      setOpen(false);
    }
    if (button.handler) {
      // Buttons reaching here always carry a function handler (the
      // `'dropdown'` case is handled separately by `wrapHandler`); the cast
      // narrows the `'dropdown' | (() => void)` union to the callable arm.
      (button.handler as () => void)();
    }
  };

  // Outside-click handler. Reproduces the class `handleClickOutside`: it always
  // closes the local state, then calls onRequestClose only when there is one AND
  // the dropdown was open at the moment of the click. `isOpen` is captured from
  // the current render closure, which equals the class getter's value at call
  // time (setState had not yet flushed when the class read `this.open`).
  useOnClickOutside(rootRef, () => {
    setOpen(false);
    // Behaviour-identical to the original `onRequestClose && isOpen &&
    // onRequestClose()` short-circuit; written as an `if` so the call is a
    // statement (the TS lint flags a bare short-circuit expression).
    if (onRequestClose && isOpen) onRequestClose();
  });

  const renderChildren = () => {
    // Matches the class: render children when controlled-open, or local-open.
    if ((useOpenProp && openProp) || open) {
      // `children` stays a literal hook: the absolutely-positioned dropdown
      // panel. Its per-variant chrome (bg, shadow, top/right offsets) is applied
      // from the root variant class as descendant variants.
      return (
        <div className="children absolute right-0 z-[1]">
          <Toolbar
            variant={`${variant}-dropdown`}
            buttons={buttons.map((button) => wrapHandler(button))}
          />
        </div>
      );
    }
  };

  const wrapHandler = (button: ToolbarButton): ToolbarButton => {
    if (button.handler === 'dropdown') {
      // `dropdown` is type-erased on the button; it carries the
      // `{ buttons, arrow }` shape for a `'dropdown'`-handled button.
      const dropdown = button.dropdown as ToolbarDropdownConfig;
      const wrappedChildren = dropdown.buttons.map((b) => wrapHandler(b));
      return Object.assign({}, button, {
        dropdown: Object.assign({}, dropdown, {
          buttons: wrappedChildren,
        }),
      });
    } else {
      return Object.assign({}, button, {
        handler: () => onButtonClick(button),
      });
    }
  };

  const renderArrow = () => {
    if (arrow) {
      // `arrow` stays a literal hook; the width clamp targets the inner
      // IconButton's literal `.icon-button` class (was `.arrow .icon-button`).
      return (
        <div className="arrow [&_.icon-button]:w-[15px]">
          <IconButton onClick={toggleOpen} variant={variant}>
            <MenuTriangle />
          </IconButton>
        </div>
      );
    }
  };

  return (
    <div className="toolbar-dropdown relative overflow-visible" ref={rootRef}>
      {renderArrow()}
      {renderChildren()}
    </div>
  );
}
